import { useState, useEffect, useRef, RefObject } from 'react';

export interface Size {
  width: number | null;
  height: number | null;
}

/**
 * Custom hook to observe element size changes using ResizeObserver API.
 * Replaces react-sizeme which uses deprecated findDOMNode.
 * 
 * @returns [ref, size] - ref to attach to element and current size
 */
export function useResizeObserver<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<Size>({ width: null, height: null });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries[0]) return;
      
      const { width, height } = entries[0].contentRect;
      setSize({ width, height });
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return [ref, size] as const;
}

/**
 * Component wrapper for size observation (similar to SizeMe component API).
 * Use this for gradual migration from react-sizeme.
 */
interface SizeMeProps {
  children: (props: { size: Size }) => React.ReactNode;
  monitorWidth?: boolean;
  monitorHeight?: boolean;
}

export function SizeMe({ children, monitorWidth = true, monitorHeight = true }: SizeMeProps) {
  const [ref, size] = useResizeObserver();

  const filteredSize: Size = {
    width: monitorWidth ? size.width : null,
    height: monitorHeight ? size.height : null,
  };

  return <div ref={ref} style={{ width: '100%', height: 'calc(100% - 20px)'  }}>{children({ size: filteredSize })}</div>;
}
